import { Box, Flex, Spinner, Text, useDisclosure } from '@chakra-ui/react';
import { getPageDataFromTemplateId } from 'api/getPageDataFromTemplateId';
import TemplatesImage from 'assets/images/templates.png';
import { UPGRADE_REDIRECT_BIZ_LINK, UPGRADE_REDIRECT_LINK } from 'constants/links';
import { CLICK_ON_PAID_FEATURE, UPGRADE } from 'constants/mixpanel';
import { usePaginatedTemplatesSearch } from 'hooks/usePaginatedTemplatesSearch/usePaginatedTemplatesSearch';
import { TemplateHit, TemplateType } from 'hooks/usePaginatedTemplatesSearch/usePaginatedTemplatesSearch.types';
import { usePrevious } from 'hooks/usePrevious';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { getUserPlanProperty } from 'libs/third-party/Mixpanel/mixpanel.helper';
import { createCopyPage } from 'modules/Editor/components/Clipboard';
import { CommonGatedUpgradeModal } from 'modules/Editor/components/Modals/CommonGatedUpgradeModal';
import { ModalUpgradeTypeEnum } from 'modules/Editor/components/Modals/CommonGatedUpgradeModal.types';
import { RootState, duplicatePage, useAppDispatch, useAppSelector } from 'modules/Editor/store';
import { store } from 'modules/Editor/store';
import { setSlideView } from 'modules/Editor/store/editorSettingsSlice';
import { useHistory } from 'modules/Editor/store/history/historyManager';
import { selectHasReachedPageLimit } from 'modules/Editor/store/infographSelector';
import { PageClipboard } from 'modules/Editor/store/pageControlSlice';
import { selectActivePage } from 'modules/Editor/store/pageSelector';
import { UserTypes, hasTemplatePermission, useUser } from 'modules/User';
import { ToastContent, useToast } from 'modules/common/components/Toast';
import { FocusEvent, ReactElement, memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setAnalyticsUpgradeFeatureProp } from 'utils/sessionStorage';
import WidgetVersionManager from 'widgets/WidgetVersionManager';

import { TemplatePageView } from './TemplatePageView/TemplatePageView';
import { TemplatesList } from './TemplatesList';
import {
  ALGOLIA_TEMPLATES_SEARCH_CONFIG,
  COLUMN_NUM,
  TEMPLATES_LIST_CLASS,
  THUMBNAIL_OVERLAY_BTN_CLASS,
} from './TemplatesMenu.config';
import { rescaleAndMapTemplates } from './TemplatesMenu.helpers';
import { TemplatesSearch } from './TemplatesSearch/TemplatesSearch';

interface TemplatesMenuProps {
  isOpen: boolean;
}

export const TemplatesMenu = memo(({ isOpen }: TemplatesMenuProps): ReactElement => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const { t } = useTranslation('editor_toolbar');
  const { t: tW } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.templatesMenu' });

  const { data } = useUser();
  const { undo } = useHistory();
  const userType = data?.user_type || UserTypes.Free;
  const hasReachedPageLimit = useAppSelector(selectHasReachedPageLimit);
  const { isOpen: isUpgradeModal, onOpen: onOpenUpgradeModal, onClose: onCloseUpgradeModal } = useDisclosure();
  const upgradeModalRef = useRef(TemplateType.Free);

  const containerRef = useRef<HTMLDivElement>(null);

  // Flag to check if the menu has been opened at least once
  // Used to trigger first algolia search for default state
  const hasBeenOpened = useRef(false);

  // Template id and page num that is currently being added
  // Format: `<TEMPLATE_ID>/<PAGE_NUMBER>`
  // If a single-page template is being added or multiple pages in multipage template, only TEMPLATE_ID is stored
  const [loadingTemplateId, setLoadingTemplateId] = useState<string | null>(null);
  const [multipageTemplate, setMultipageTemplate] = useState<TemplateHit | null>(null);
  const prevMultipageTemplate = usePrevious(multipageTemplate);

  const [enableAlgoliaSearch, setEnableAlgoliaSearch] = useState(false);
  // Used to keep same scroll position in templates list when switching between multipage templates view + templates list
  const previousScrollTop = useRef(0);
  const visibleRowStartIndex = useRef(0);

  const { templates, searchTemplates, onShowMore, query, filters, setFilters, hasNoResultsFound, isLoadingMore } =
    usePaginatedTemplatesSearch({
      ...ALGOLIA_TEMPLATES_SEARCH_CONFIG,
      useInfographTemplates: !enableAlgoliaSearch,
    });

  // Trigger algolia search for default state when the menu is first opened
  useEffect(() => {
    if (isOpen && !hasBeenOpened.current) {
      searchTemplates('');
      hasBeenOpened.current = true;
    }
  }, [isOpen, searchTemplates]);

  // Focus on first visible template when multipageTemplate is closed
  useEffect(() => {
    if (!!prevMultipageTemplate && !multipageTemplate && isOpen) {
      containerRef.current?.focus();
    }
  }, [prevMultipageTemplate, multipageTemplate, isOpen]);

  const handleFocus = (e: FocusEvent) => {
    if (!e.target?.contains(containerRef.current)) return;

    // Focus on first visible element if tabbing from search/filter input
    // or coming from multipage template view
    const relatedTargetClassList = e.relatedTarget?.classList;
    if (
      !e.relatedTarget ||
      (!relatedTargetClassList?.contains(THUMBNAIL_OVERLAY_BTN_CLASS) &&
        !relatedTargetClassList?.contains(TEMPLATES_LIST_CLASS))
    ) {
      const firstVisibleTemplateId = templates?.[visibleRowStartIndex.current * COLUMN_NUM]?.template_id;
      if (firstVisibleTemplateId) {
        e.stopPropagation();
        e.preventDefault();

        containerRef.current?.blur();

        const templateThumbnail = document.getElementById(`${firstVisibleTemplateId}-thumbnail`);
        const templateThumbnailBtn = templateThumbnail?.querySelector(`.${THUMBNAIL_OVERLAY_BTN_CLASS}`) as HTMLElement;
        templateThumbnailBtn?.focus();
      }
    }
  };
  /**
   * Triggered when clicking on template thumbnail
   * If user doesn't have permission for the template, they will be
   * redirected to subscriptions page.
   *
   * @param templateId Template id
   * @param index index in `templates` array
   * @param pageNumbers number[] - page numbers to add
   * @returns
   */
  const onSelectTemplate = async (templateData: TemplateHit, pageNumbers?: number[]) => {
    const { infograph } = store.getState();

    // Only allow adding one page at a time
    if (!!loadingTemplateId) {
      return;
    }

    const {
      type,
      isMultipage: isMultipageAlgolia,
      template_id: templateId,
      width: widthPx,
      height: heightPx,
    } = templateData;
    const isMultipage = isMultipageAlgolia === 1;

    // Redirect user if they don't have permission for the template
    const templatePermission = type;
    if (!hasTemplatePermission(userType, templatePermission)) {
      upgradeModalRef.current = templatePermission;
      onOpenUpgradeModal();
      const mpFeatureString =
        templatePermission === 'premium' ? UPGRADE.feature.TemplatePremium : UPGRADE.feature.TemplateBusiness;
      Mixpanel.track(CLICK_ON_PAID_FEATURE.eventName, {
        Feature: mpFeatureString,
        plan: getUserPlanProperty(userType),
      });
      return;
    }

    // If multipage template and no page numbers specified, render page view
    if (isMultipage && !pageNumbers) {
      setMultipageTemplate(templateData);
      return;
    }

    const isAddingMultiplePages = pageNumbers && pageNumbers.length > 1;
    try {
      const newLoadingTemplateId =
        !isMultipage || (pageNumbers && pageNumbers.length > 1) ? templateId : `${templateId}/${pageNumbers?.[0] || 1}`;
      setLoadingTemplateId(newLoadingTemplateId);

      const pages = await getPageDataFromTemplateId(templateId, pageNumbers || [1]);

      // Create copy page data and update the widgets for each page being added
      // Note: copy page data will only include the widgets that are on the page and filters
      // out any deleted widgets
      const copyPageData: PageClipboard[] = [];
      pages.forEach((page) => {
        const [copyPage] = createCopyPage(page.pageData, page.widgetDataMap);
        copyPage.widgets = WidgetVersionManager.update(copyPage.widgets);
        const batchWidgetUpdate = rescaleAndMapTemplates(copyPage.widgets, infograph.size, { widthPx, heightPx });
        copyPageData.push({
          ...copyPage,
          widgets:
            batchWidgetUpdate && Object.keys(batchWidgetUpdate).length > 0 ? batchWidgetUpdate : copyPage.widgets,
        });
      });

      const activePage = selectActivePage(store.getState() as RootState);
      dispatch(
        duplicatePage({
          insertAfterId: activePage,
          pageClipboards: copyPageData,
        }),
      );

      const toastId = `add-page-toast-${activePage}`;

      const handleClick = () => {
        if (undo) undo();
        toast.close(toastId);
      };

      if (isAddingMultiplePages) {
        toast({
          id: toastId,
          title: 'Pages added',
          render: ({ title }) => (
            <ToastContent title={title} buttonTitle={t('undoBtn.text')} handleClick={handleClick} />
          ),
        });
      } else {
        toast({
          id: toastId,
          title: t('pageAction.add'),
          render: ({ title }) => (
            <ToastContent title={title} buttonTitle={t('undoBtn.text')} handleClick={handleClick} />
          ),
        });
      }

      const mpProps = {
        from: 'Templates',
        type: 'Template',
        template_id_new_page: templateId,
        all_template_pages: isAddingMultiplePages,
      };
      Mixpanel.track('Page Added', mpProps);
    } catch (error) {
      console.error(`Failed to add page from template ${templateId}`, error);
      toast({
        title: 'Failed to add page from template',
        status: 'error',
        render: () => <ToastContent title={'Failed to add page from template'} status={'error'} />,
      });
    } finally {
      // Open PM if multiple pages are added
      if (isAddingMultiplePages) {
        dispatch(setSlideView(true));
      }
      setLoadingTemplateId(null);
    }
  };

  /**
   * Handle search input
   * Resets multipage template
   */
  const onSearch = useCallback(
    (query: string) => {
      if (!query) {
        setEnableAlgoliaSearch(false);
      } else {
        setEnableAlgoliaSearch(true);
      }

      setMultipageTemplate(null);
      searchTemplates?.(query);
    },
    [searchTemplates],
  );

  const resetMultipageTemplate = () => {
    setMultipageTemplate(null);
  };
  const updatePreviousScrollTop = (scrollTop: number) => (previousScrollTop.current = scrollTop);
  const updateVisibleRowStartIdx = (idx: number) => (visibleRowStartIndex.current = idx);

  const onClickUpgrade = () => {
    const templateType = upgradeModalRef.current;
    const mpFeatureString =
      templateType === 'premium' ? UPGRADE.feature.TemplatePremium : UPGRADE.feature.TemplateBusiness;
    Mixpanel.track(UPGRADE.eventName, { Feature: mpFeatureString });
    // using window because it's not possible to use the hook useChameleon outside a react component.
    if (!!window.chmln) {
      window.chmln.track(UPGRADE.eventName);
    }

    // Set mixpanel_feature session storage item used by `Analytics: Upgrade` event
    setAnalyticsUpgradeFeatureProp(mpFeatureString);
    const link = templateType === UserTypes.Business ? UPGRADE_REDIRECT_BIZ_LINK : UPGRADE_REDIRECT_LINK;

    window.open(link, '_self');
  };

  return (
    <Flex
      h='full'
      w='full'
      overflow='hidden'
      p={2}
      pr={0}
      direction='column'
      alignItems='center'
      display={isOpen ? 'flex' : 'none'}
    >
      <CommonGatedUpgradeModal
        isOpen={isUpgradeModal}
        onClose={onCloseUpgradeModal}
        onClickUpgrade={onClickUpgrade}
        upgradePlan={upgradeModalRef.current === UserTypes.Business ? UserTypes.Business : UserTypes.Premium}
        modalImg={TemplatesImage}
        modalType={ModalUpgradeTypeEnum.Templates}
      />
      {multipageTemplate && (
        <TemplatePageView
          templateData={multipageTemplate}
          onExit={resetMultipageTemplate}
          onAddPages={onSelectTemplate}
          addPageIsDisabled={hasReachedPageLimit || !!loadingTemplateId}
          loadingTemplateId={loadingTemplateId}
        />
      )}
      {!multipageTemplate && (
        <>
          <TemplatesSearch onSearch={onSearch} query={query} filters={filters} setFilters={setFilters} />
          {hasNoResultsFound && (
            <Text role='status' aria-live='polite' p={2} fontSize='sm' fontWeight='semibold' w='full' textAlign='left'>
              {tW('search.noResultFound', { query })}
            </Text>
          )}
          {!hasNoResultsFound && (
            <>
              {query && (
                <Text p={2} fontSize='sm' fontWeight='semibold' w='full' textAlign='left'>
                  {tW('search.resultFound', { query })}
                </Text>
              )}
              <Box flex='1' minH={0} w='full' tabIndex={0} onFocus={handleFocus} ref={containerRef}>
                <TemplatesList
                  templates={templates}
                  onSelectTemplate={onSelectTemplate}
                  userType={userType}
                  addPageIsDisabled={hasReachedPageLimit || !!loadingTemplateId}
                  loadingTemplateId={loadingTemplateId}
                  loadMore={onShowMore}
                  initialScrollTop={previousScrollTop.current || 0}
                  updatePreviousScrollTop={updatePreviousScrollTop}
                  updateVisibleRowStartIdx={updateVisibleRowStartIdx}
                />
              </Box>
              {isLoadingMore && (
                <Spinner data-testid='templates-widgetmenu-spinner' alignSelf='center' color='brand.500' />
              )}
            </>
          )}
        </>
      )}
    </Flex>
  );
});

TemplatesMenu.displayName = 'TemplatesMenu';
